import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { TableRow as MuiTableRow } from '@mui/material'
import { useState, type RefObject, useEffect } from 'react'
import { PinnedCell } from 'shared/ui/TableNew/ui/PinnedCell/PinnedCell'
import { type IColumn, type IRowDefault, type ITableProps } from 'shared/ui/TableNew'
import { ActionsRow } from 'shared/ui/TableNew/ui/TableRow/ui/ActionsRow'
import { ColumnCell, DefaultRenderRowCell } from 'shared/ui/TableNew/ui/TableRow/ui/ColumnCell'
import { getAriaLabel } from 'shared/lib'
import commonStyles from 'shared/ui/TableNew/ui/Table.module.scss'
import styles from './styles.module.scss'

export type ITableRowChildProps<TRow extends IRowDefault> = {
  pinnedColumns?: IColumn<TRow>[]
  unpinnedColumns?: IColumn<TRow>[]
  hideExpandColumnsIds?: ITableProps<TRow>['hideExpandColumnsIds']
  row: TRow
  selected?: boolean
  isScrollLeft?: boolean
  inAction?: boolean
  isHoveredRowActions?: boolean
  isCursor?: boolean
  onRowClick?: ITableProps<TRow>['onRowClick']
  getRowLink?: ITableProps<TRow>['getRowLink']
  activeRowId?: ITableProps<TRow>['activeRowId']
  isCheckDate?: ITableProps<TRow>['isCheckDate']
  defaultWidthColumn?: ITableProps<TRow>['defaultWidthColumn']
  defaultMinWidthColumn?: ITableProps<TRow>['defaultMinWidthColumn']
  defaultMaxWidthColumn?: ITableProps<TRow>['defaultMaxWidthColumn']
  refTable: RefObject<HTMLDivElement>
}

export const TableRowChild = observer(
  <TRow extends IRowDefault>({
    pinnedColumns = [],
    unpinnedColumns = [],
    hideExpandColumnsIds = [],
    row,
    inAction,
    onRowClick,
    activeRowId,
    isCheckDate,
    getRowLink,
    defaultWidthColumn,
    defaultMinWidthColumn,
    defaultMaxWidthColumn,
    isCursor,
    isHoveredRowActions,
    refTable,
    ...props
  }: ITableRowChildProps<TRow>) => {
    const [isHover, setIsHover] = useState(false)
    const [isRightOptionsOpen, setIsRightOptionsOpen] = useState(false)
    const isScrollLeft = props.isScrollLeft || false
    const selected = props.selected || false

    useEffect(() => {
      if (row.init) {
        return row.init()
      }
    }, [])

    const columnCellRender = (column: IColumn<TRow>, colSpan?: number) => {
      return (
        <ColumnCell
          row={row}
          column={column}
          getRowLink={getRowLink}
          onRowClick={onRowClick}
          defaultMinWidthColumn={defaultMinWidthColumn}
          defaultMaxWidthColumn={defaultMaxWidthColumn}
          defaultWidthColumn={defaultWidthColumn}
          colSpan={colSpan}
          isCursor={isCursor}
          isExpandedChild
        >
          {(ref) => (
            <DefaultRenderRowCell
              row={row}
              column={column}
              hideExpandColumnsIds={hideExpandColumnsIds}
              isHover={isHover}
              isCheckDate={isCheckDate}
              refTable={refTable}
              refCell={ref}
            />
          )}
        </ColumnCell>
      )
    }
    const activeRow = row.id === activeRowId

    return (
      <MuiTableRow
        key={row.id}
        aria-label={row.ariaLabel ? getAriaLabel(row.ariaLabel) : undefined}
        className={classNames(styles.row, commonStyles.tableRow, {
          [commonStyles.activeRow]: activeRow,
          [commonStyles.selectedRow]: selected,
          [commonStyles.inActionRow]: inAction,
          [commonStyles.tableRowHovered]: isRightOptionsOpen || isHover,
        })}
        onMouseEnter={() => {
          setIsHover(true)
        }}
        onMouseLeave={() => {
          setIsHover(false)
        }}
      >
        {Boolean(pinnedColumns.length) && (
          <PinnedCell
            isScrollLeft={isScrollLeft}
            width={!pinnedColumns.length ? 40 : undefined}
            withEmptyCell={pinnedColumns.length === 1}
          >
            {pinnedColumns.map((column) => columnCellRender(column))}
          </PinnedCell>
        )}
        {unpinnedColumns.map((column, index, self) =>
          columnCellRender(column, index === self.length - 1 && !row.withActions ? 2 : undefined)
        )}

        {row.withActions && (
          <ActionsRow
            row={row}
            isHoveredRowActions={isHoveredRowActions}
            isRightOptionsOpen={isRightOptionsOpen}
            setIsRightOptionsOpen={setIsRightOptionsOpen}
          />
        )}
      </MuiTableRow>
    )
  }
)
